// intentDispatch — the UI shell's ONE trigger choke point.
//
// Pre-M2 this routed a bare `KeyAction`. Determinism M2 widened it to the
// reified `UiTrigger` (trigger.ts): every routed signal arrives with its
// provenance and arrival time as explicit data, through this single door.
//
// Routing stays pure over explicit dependencies. Each `TriggeredAction`
// variant maps to exactly one route; clocks, id generation and I/O stay out of
// the React handlers this feeds.

import type * as A from "@bilda/app";
import type { CommandId, UiIntent } from "@bilda/infra/interaction";
import { exhaustSwitchCase } from "@bilda/utils";
import type { ChatCommand } from "../modules/chat.js";
import type { FrameTransition } from "./frame.js";
import type { IngestCommand } from "./ingestDecider.js";
import type { UiTrigger } from "./trigger.js";

// The resolved input shape for a routed domain command — derived from the app
// contract, browser-safe (no vendor SDK, no `@bilda/app` runtime import).
type AppCommandInput = A.InputOf<A.AppContract[keyof A.AppContract]>;

// One route per `TriggeredAction` variant. `key` keeps the pre-M2 KeyAction
// contract (its own ui/domain split lives inline); `chat`, `frame` and `ingest`
// route to the pure deciders (the shell interprets their effects).
export type IntentRoutes = {
    readonly ui: (intent: UiIntent) => void;
    readonly domain: (command: CommandId, input: AppCommandInput) => void;
    readonly chat: (command: ChatCommand) => void;
    readonly frame: (transition: FrameTransition) => void;
    readonly ingest: (command: IngestCommand) => void;
};

export type IntentDispatcher = (
    trigger: UiTrigger,
    resolvedInput?: AppCommandInput,
) => void;

export const createIntentDispatcher =
    (routes: IntentRoutes): IntentDispatcher =>
    (trigger, resolvedInput) => {
        const action = trigger.action;
        switch (action.type) {
            case "key": {
                const key = action.action;
                if (key.type === "domain") {
                    if (resolvedInput === undefined) {
                        throw new Error(
                            `domain action ${key.command} requires resolved input`,
                        );
                    }
                    routes.domain(key.command, resolvedInput);
                    return;
                }
                routes.ui(key);
                return;
            }
            case "chat":
                routes.chat(action.command);
                return;
            case "frame":
                routes.frame(action.transition);
                return;
            case "ingest":
                routes.ingest(action.command);
                return;
            default:
                return exhaustSwitchCase(action);
        }
    };
